import { OdmSettings, OdmStage } from '@droidmap/shared-odm-settings'

export enum MappingJobStatus {
  NOT_STARTED = 'NOT_STARTED',
  STARTING_COMPUTE_INSTANCE = 'STARTING_COMPUTE_INSTANCE',
  TRANSFERRING_INPUTS = 'TRANSFERRING_INPUTS',
  IN_PROGRESS = 'IN_PROGRESS',
  TRANSFERRING_OUTPUTS = 'TRANSFERRING_OUTPUTS',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  INTERRUPTED = 'INTERRUPTED',
  /**
   * Stopped at a user's request (droidmap#2982). Terminal, like COMPLETED and
   * FAILED. Written only by the pipeline's `Job Cancelled` end state — the
   * cancel route records {@link MappingJob.cancelRequestedAt} and leaves the
   * status alone until the pipeline has actually torn the job down.
   */
  CANCELLED = 'CANCELLED',
}

/**
 * Statuses a job never leaves. A cancel against one of these is a no-op.
 *
 * INTERRUPTED is deliberately absent: it is the pipeline's spot / OOM retry
 * path, and the job runs again from `Start Compute`.
 */
export const TERMINAL_MAPPING_JOB_STATUSES: ReadonlySet<MappingJobStatus> =
  new Set([
    MappingJobStatus.COMPLETED,
    MappingJobStatus.FAILED,
    MappingJobStatus.CANCELLED,
  ])

export const isTerminalMappingJobStatus = (status: MappingJobStatus) =>
  TERMINAL_MAPPING_JOB_STATUSES.has(status)

/**
 * How long a cancel request is treated as in flight. The pipeline's canceller
 * runs for up to 14 minutes and EventBridge retries it twice, so a request
 * older than this has run its whole course: either the job is about to be
 * terminal, or the canceller gave up and a repeat should be sent.
 */
export const CANCEL_REQUEST_RETRY_AFTER_MS = 45 * 60 * 1000

/**
 * Whether a cancel requested at `cancelRequestedAt` is still being worked on.
 * The service uses it to decide whether a repeat is a no-op; the web uses it
 * to show "Cancelling…" rather than offer the action again.
 */
export const isCancelRequestInFlight = (
  cancelRequestedAt: Date | string | undefined,
  now: number = Date.now(),
): boolean =>
  cancelRequestedAt !== undefined &&
  now - new Date(cancelRequestedAt).getTime() < CANCEL_REQUEST_RETRY_AFTER_MS

export type ComputeResources = {
  cpuAndMemory?: {
    vCpus: number
    memory: number
  }
  /**
   * Opt-*out* of on-demand capacity. Absent (the default) routes the job
   * to on-demand; only an explicit `false` requests the spot queue.
   *
   * Interpreted in exactly two Lambdas, which must agree —
   * `start-compute-instance.ts` picks the Batch job queue and
   * `get-compute-instance-details.ts` picks the matching compute
   * environment. `instanceTypeOverride` (benchmark runs) wins over this
   * flag in both.
   */
  useOnDemand?: boolean
  /**
   * Re-run this job with double the memory its input size would normally be
   * provisioned with.
   *
   * Stamped by the pipeline's `Escalate Memory` state after an
   * {@link ODM_OUT_OF_MEMORY_ERROR}, which resubmits the job once at the next
   * band up. Interpreted only by `start-compute-instance.ts`.
   *
   * **Doubling is the only move available.** The Graviton families step
   * `c` -> `m` -> `r` at 2, 4 and 8 GiB per vCPU, so at a fixed vCPU count the
   * next box up has exactly twice the memory — there is no 10%-more instance
   * to ask for. That is also why this is reactive rather than pre-emptive:
   * escalating every job a model flagged would pay double on all of them to
   * save the fraction that actually die.
   *
   * Its presence is also the once-only guard — the pipeline's Choice requires
   * it to be absent, so an escalated job that OOMs again fails rather than
   * climbing forever. The Choice additionally refuses a job carrying
   * `cpuAndMemory`: `start-compute-instance` short-circuits on that before it
   * sizes anything, so such a job would be retried at identical memory and
   * burn a full duplicate run before failing.
   */
  escalateMemory?: boolean
}

export type TaskReference = {
  taskId: string
  referenceId: string
}

// If you modify this type then you will probably need to modify the
// attributes section in the {MappingJobEntity}.
export type MappingJob = {
  /**
   * The unique identifier for the mapping job.
   */
  id: string

  /**
   * The associated task ID for this mapping job.
   */
  taskId: string

  /**
   * The status of the mapping job.
   * @default NOT_STARTED
   */
  status: MappingJobStatus

  /**
   * The input asset sets for the mapping job.
   */
  inputAssetSets: string[]

  /**
   * The output asset sets for the mapping job.
   */
  outputAssetSets: string[]

  /**
   * The boundary asset ID for the mapping job.
   */
  boundaryAssetId?: string

  /**
   * The GCP (Ground Control Point) asset ID for the mapping job.
   * This references an asset containing the GCP file (e.g., gcp_list.txt).
   */
  gcpAssetId?: string

  /**
   * The OpenDroneMap (ODM) settings for the mapping job.
   */
  odmSettings?: OdmSettings

  /**
   * The compute resources override for the mapping job.
   * This is optional and if not provided then we try to calculate the
   * required compute resources based on the input asset sets.
   */
  computeResourcesOverride?: ComputeResources

  /**
   * Pin the job to a specific EC2 instance type (e.g. `c7g.4xlarge`).
   * When set, the job is routed to a dedicated benchmarking AWS Batch
   * compute environment that is restricted to this instance type only,
   * so we can compare like-for-like across configurations.
   *
   * Used for ODM compute benchmarking; leave unset for normal jobs so
   * Batch's best-fit allocator picks from the standard family list.
   */
  instanceTypeOverride?: string

  /**
   * Storage backing for the EC2 working volume mounted at `/data`.
   * Only consulted when `instanceTypeOverride` is also set — ignored
   * for normal (non-benchmark) jobs.
   *
   * - `nvme` (default): instance-store NVMe device. Requires a
   *   `d`-suffix instance type (e.g. `c6id.4xlarge`).
   * - `ebs`: 2 TB gp3 EBS volume attached by the EBS-variant launch
   *   template. Required for non-`d` instance types and used to
   *   benchmark EBS vs NVMe head-to-head on types where both options
   *   are possible.
   * - `efs`: Amazon EFS (One Zone, Bursting) NFS-mounted at `/data` by
   *   the EFS-variant launch template, swap on a small gp3 volume.
   *   Dev-only, for ADR-0022's EFS-vs-gp3 benchmark (droidmap#2847).
   *   Same non-`d` constraint as `ebs`.
   */
  storage?: 'nvme' | 'ebs' | 'efs'

  /**
   * Run the job on an NVIDIA-CUDA GPU instance using the GPU variant
   * of the ODM container. Routes the job to the permanent GPU spot /
   * on-demand queues (or, when `instanceTypeOverride` is also set, to
   * the matching single-instance-type GPU benchmark queue).
   *
   * Defaults to false — CPU jobs are unaffected. When true and
   * `instanceTypeOverride` is set to a non-GPU family, start-compute-
   * instance rejects the submission rather than silently downgrading.
   */
  useGpu?: boolean

  /**
   * When a user asked for this job to be cancelled (droidmap#2982). Set by
   * `POST /mapping-jobs/:id/cancel` while the job is still running; the
   * status only becomes CANCELLED once the pipeline has terminated the Batch
   * job and released its file system. Present but with a non-CANCELLED
   * status means "Cancelling…".
   */
  cancelRequestedAt?: Date

  /**
   * The user id of the user who asked for the cancel.
   */
  cancelRequestedBy?: string

  /**
   * The time the entity was created. This attribute is automatically
   * set by the dynamodb-onetable library.
   */
  created: Date

  /**
   * The user id of the user who created this entity.
   */
  createdBy: string

  /**
   * The last time this entity was modified. This attribute is automatically
   * set by the dynamodb-onetable library.
   */
  updated: Date

  /**
   * The user id of the user who last updated this entity.
   */
  updatedBy: string
}

export type MappingJobProgress = {
  mappingJobId: string
  taskId: string
  status: MappingJobStatus
  executionId?: string
  odmStage?: OdmStage
  error?: {
    Error: string
    Cause: string
  }
}

export type CreateMappingJobInput = Omit<
  MappingJob,
  'id' | 'status' | 'created' | 'updated' | 'createdBy' | 'updatedBy'
>

export type GetMappingJobInput = {
  mappingJobId: string
}

export type DeleteMappingJobInput = {
  mappingJobId: string
}

export type CancelMappingJobInput = {
  mappingJobId: string
}

/**
 * Detail of {@link MappingJobCancelRequested}. Carries the status the job was
 * in when the cancel was asked for so the pipeline can log the stage without
 * reading the job back, and when it was asked for so project-service can
 * record it on the task.
 */
export type MappingJobCancelRequest = {
  mappingJobId: string
  taskId: string
  status: MappingJobStatus
  /** ISO timestamp — the job's own `cancelRequestedAt`. */
  cancelRequestedAt: string
}
